import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import translations, { type Lang } from "./translations";

type TranslationValue = string | string[] | Record<string, any> | readonly any[];

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => any;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const saved = localStorage.getItem("site-lang");
      if (saved === "he" || saved === "ar") return saved;
    } catch {}
    return "ar";
  });

  const setLang = useCallback((newLang: Lang) => {
    setLangState(newLang);
    try {
      localStorage.setItem("site-lang", newLang);
    } catch {}
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    // Update font class on body for public pages
    if (lang === "ar") {
      document.body.classList.add("lang-ar");
      document.body.classList.remove("lang-he");
    } else {
      document.body.classList.add("lang-he");
      document.body.classList.remove("lang-ar");
    }
  }, [lang]);

  const t = useCallback(
    (key: string): any => {
      const keys = key.split(".");
      let value: any = translations[lang];
      for (const k of keys) {
        if (value == null) return key;
        value = value[k];
      }
      return value ?? key;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
